import { DashboardGridLayoutItem, DashboardGridLayoutItemConfigurationInput } from '../views/dashboard-view.component';
import { CompactType, cloneLayout, compact } from './dashboard-react-grid-layout-utils';
import { Dashboard } from './dashboard.model';

export function constructDashboardTileLayoutConfigurationInput(
   newLayout: DashboardGridLayoutItem[] | null,
   currentLayout: DashboardGridLayoutItem[] | null
): DashboardGridLayoutItemConfigurationInput[] {
   if (!newLayout) return [];

   const addedTiles = [...(newLayout ?? [])].filter(
      (ntl) => ![...(currentLayout ?? [])].some((tl) => ntl.id === tl.id)
   );

   // QMG-5556: Ignoring unexpected moved and static keys which come from katoid grid event in finicky grid interactions
   const ignoreKeys = ['isDirty', 'dashboardTileId', 'moved', 'static'];

   return [
      ...(currentLayout ?? []).map((tl) => {
         const foundNewTileLayout = newLayout.find((ntl) => ntl.id === tl.id);

         if (foundNewTileLayout) {
            const dirtyKeys = Object.keys(tl).filter((k) => !ignoreKeys.includes(k) && tl[k] !== foundNewTileLayout[k]);
            return {
               ...tl,
               ...foundNewTileLayout,
               isDirty: dirtyKeys.length > 0,
               fields: dirtyKeys,
            };
         }

         throw new Error('Cannot map a dashboard tile layout configuration not found in selected dashboard');
      }),
      ...addedTiles.map((atl) => {
         const dirtyKeys = Object.keys(atl).filter((k) => !ignoreKeys.includes(k));
         return {
            ...atl,
            isDirty: dirtyKeys.length > 0,
            fields: dirtyKeys,
         };
      }),
   ];
}

export function getPreservedLayoutFromDashboard(dashboard: Dashboard | null): DashboardGridLayoutItem[] {
   return (dashboard?.dashboardTiles ?? [])
      .map((dt) => {
         return {
            id: dt.dashboardTileLayoutConfiguration.dashboardTileLayoutConfigurationId,
            dashboardTileId: dt.dashboardTileId,
            h: dt.dashboardTileLayoutConfiguration.height,
            w: dt.dashboardTileLayoutConfiguration.width,
            x: dt.dashboardTileLayoutConfiguration.x,
            y: dt.dashboardTileLayoutConfiguration.y,
            maxH: dt.dashboardTileLayoutConfiguration.maxHeight ?? undefined,
            minH: dt.dashboardTileLayoutConfiguration.minHeight ?? undefined,
            maxW: dt.dashboardTileLayoutConfiguration.maxWidth ?? undefined,
            minW: dt.dashboardTileLayoutConfiguration.minWidth ?? undefined,
         };
      })
      .sort((a, b) => a.id - b.id);
}

export function mapLayoutToTargetColumns(
   currentCols: number,
   targetCols: number,
   currentLayout: DashboardGridLayoutItem[]
): DashboardGridLayoutItem[] {
   if (targetCols === currentCols || currentLayout.length === 0) return currentLayout;

   const mappingDown = targetCols < currentCols;
   const mappedLayout = [...(currentLayout ?? [])].map((l) => {
      return {
         ...l,
         w: mappingDown ? Math.min(l.w, targetCols) : Math.floor(l.w),
      };
   });

   const sortedRows: DashboardGridLayoutItem[][] = getSortedLayoutByRowThenColumns(mappedLayout);
   if (sortedRows.length === 0) return [];

   let currRowsLastColumn: number;
   let currRow: number = 0;
   let currRowsBiggestHeight: number;
   const newLayout: DashboardGridLayoutItem[] = [];
   sortedRows.forEach((row) => {
      currRowsLastColumn = row[0].x;
      currRowsBiggestHeight = row[0].h;
      row.forEach((l) => {
         currRowsBiggestHeight = Math.max(l.h, currRowsBiggestHeight);
         if (l.w + currRowsLastColumn > targetCols) {
            currRow += currRowsBiggestHeight;
            currRowsLastColumn = 0;
            currRowsBiggestHeight = l.h;
         }
         const widthSpan = l.x + l.w;
         l.x = widthSpan > targetCols ? currRowsLastColumn : l.x;
         l.y = currRow;
         currRowsLastColumn = widthSpan;
         newLayout.push(l);
      });
      currRow += 1;
   });
   return [...newLayout];
}

export function getSortedLayoutByRowThenColumns(layout: DashboardGridLayoutItem[]): DashboardGridLayoutItem[][] {
   if (layout.length === 0) return [];
   const sortedLayout: DashboardGridLayoutItem[] = [...layout].sort((a, b) => a.y - b.y);
   const rows: DashboardGridLayoutItem[][] = [];
   let row: DashboardGridLayoutItem[] = [];
   let currRow = sortedLayout[0].y;
   sortedLayout.forEach((l) => {
      if (l.y !== currRow) {
         currRow = l.y;
         rows.push(row);
         row = [];
      }
      row.push(l);
   });
   if (row.length > 0) rows.push(row);

   return rows.map((row) => row.sort((a, b) => a.x - b.x));
}

export function compactLayout(
   layout: DashboardGridLayoutItem[],
   compactType: CompactType,
   cols: number
): DashboardGridLayoutItem[] {
   // Need to clone layout first before compacting, katoid throws an error sometimes without - NDT-18727
   const compactLayout = compact(cloneLayout(layout), compactType, cols);
   return layout.map((l) => {
      const found = compactLayout.find((cl) => cl.id === l.id);
      return found ? { ...l, ...found, id: l.id } : l;
   });
}
